import React, { useState, useRef, useEffect, useCallback } from 'react';
import StoryModal from './StoryModal';
import styles from './StoriesSection.module.css';
const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';
const MAX_VISIBLE    = 10;
const HERO_SECTION_HEIGHT = 520;   // adjust to match your hero section bottom
const HEADER_HEIGHT  = 70;
const REFRESH_MS     = 5 * 60 * 1000; // re-fetch every 5 min

function getToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
}

// ── Resolve any media URL to the correct backend host ────────────────────────
// Backend may return http://127.0.0.1:8000/... but the network uses 192.168.56.1
const BACKEND_HOST = process.env.REACT_APP_API_URL
  ? new URL(process.env.REACT_APP_API_URL).origin
  : 'https://imselling-marketplace-production.up.railway.app';

function resolveMediaUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('http://192.168.56.1') || url.startsWith('https://')) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('http://127.0.0.1') || url.startsWith('http://localhost')) {
    return url.replace(/^http:\/\/(127\.0\.0\.1|localhost)(:\d+)?/, BACKEND_HOST);
  }
  if (url.startsWith('/')) return `${BACKEND_HOST}${url}`;
  return `${BACKEND_HOST}/${url}`;
}

// ─── Transform a backend status-group → StoryModal shape ─────────────────────
function groupToStory(group) {
  const name       = group.user.username || group.user.email || 'User';
  const firstStatus = group.statuses[0];

  // Resolve thumbnail: prefer thumbnail_url (still frame), then media_url for images
  // For videos without thumbnail, we pass the video src so StoryAvatar can render a frame
  const thumbUrl = resolveMediaUrl(firstStatus?.thumbnail_url);
  const mediaUrl = resolveMediaUrl(firstStatus?.media_url);
  const isVideo  = firstStatus?.media_type === 'video';

  return {
    id:       group.user.id,
    username: name,
    // ✅ FIXED: resolved URL for actual content preview
    // thumbUrl = still frame (generated by backend or captured by browser)
    // mediaUrl = fallback for images; for videos used in StoryAvatar element
    thumbUrl:  thumbUrl || (!isVideo ? mediaUrl : null),
    videoSrc:  isVideo  ? mediaUrl : null,   // used to render first frame in pill
    isVideo:   isVideo,
    isNew:     group.has_unviewed,
    userEmail: group.user.email,
    // Each individual status becomes one slide
    stories: group.statuses.map(s => ({
      id:         s.id,
      type:       s.media_type,
      url:        resolveMediaUrl(s.media_url),
      thumbnail:  resolveMediaUrl(s.thumbnail_url) || resolveMediaUrl(s.media_url),
      duration:   s.media_type === 'video' ? 15000 : 5000,
      caption:    s.caption    || '',
      expires_at: s.expires_at,
      view_count: s.view_count,
    })),
  };
}

// ─── StoryAvatar — shows actual content (image thumb or video first frame) ────
// For images: renders <img src={thumbUrl} />
// For videos without thumbnail: renders muted <video> — browser paints first frame
// Falls back to ui-avatars initials if both are null.
function StoryAvatar({ story, className, size = 48 }) {
  const [errored, setErrored] = React.useState(false);
  const fallbackSrc = `https://ui-avatars.com/api/?name=${encodeURIComponent(story.username)}&background=25D366&color=fff&size=80`;

  if (!errored && story.thumbUrl) {
    return (
      <img
        src={story.thumbUrl}
        alt={story.username}
        className={className}
        onError={() => setErrored(true)}
      />
    );
  }

  if (!errored && story.videoSrc) {
    // Muted autoplay video — browser shows first decoded frame even when autoplay
    // is throttled; onTimeUpdate loops within 10s like MessagesTab VideoThumb
    return (
      <video
        src={story.videoSrc}
        muted
        autoPlay
        playsInline
        loop
        preload="metadata"
        className={className}
        style={{ objectFit: 'cover' }}
        onTimeUpdate={e => { if (e.target.currentTime >= 10) e.target.currentTime = 0; }}
        onError={() => setErrored(true)}
      />
    );
  }

  // Fallback: initials avatar
  return (
    <img
      src={fallbackSrc}
      alt={story.username}
      className={className}
    />
  );
}

// ─── StoriesSection ───────────────────────────────────────────────────────────
const StoriesSection = () => {
  const [stories, setStories]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [isModalOpen, setIsModalOpen]     = useState(false);
  const [scrollY, setScrollY]             = useState(0);
  const [dragPos, setDragPos]             = useState(null); // null = CSS default

  const isDragging  = useRef(false);
  const dragOffset  = useRef({ x: 0, y: 0 });
  const hasDragged  = useRef(false);
  const pillRef     = useRef(null);

  // ── Fetch real statuses ─────────────────────────────────────────────────────
  const fetchStatuses = useCallback(async () => {
    try {
      const token   = getToken();
      const headers = token ? { Authorization: `Token ${token}` } : {};
      const res     = await fetch(`${API_BASE}/statuses/`, { headers });

      if (res.ok) {
        const data   = await res.json();
        const groups = (data.groups || []).slice(0, MAX_VISIBLE);
        setStories(groups.map(groupToStory));
      }
    } catch (e) {
      console.error('StoriesSection: fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
    const timer = setInterval(fetchStatuses, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchStatuses]);

  // ── Mark viewed when a story is opened ─────────────────────────────────────
  const markViewed = useCallback(async (story) => {
    const token = getToken();
    if (!token) return;
    for (const slide of story.stories) {
      try {
        await fetch(`${API_BASE}/statuses/${slide.id}/view/`, {
          method: 'POST',
          headers: { Authorization: `Token ${token}` },
        });
      } catch (_) {}
    }
    // Optimistically remove the "new" ring
    setStories(prev =>
      prev.map(s => s.id === story.id ? { ...s, isNew: false } : s)
    );
  }, []);

  // ── Scroll tracking ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const heroBottomInViewport = HERO_SECTION_HEIGHT - scrollY;
  const pillH                = pillRef.current?.offsetHeight || 100;
  const isHeroVisible        = heroBottomInViewport > HEADER_HEIGHT + pillH;

  // ── Drag ────────────────────────────────────────────────────────────────────
  const clampAndSet = (clientX, clientY) => {
    const pill  = pillRef.current;
    const pillW = pill?.offsetWidth  || 400;
    const pH    = pill?.offsetHeight || 100;
    const pageY = clientY + window.scrollY;
    const newX  = Math.max(0, Math.min(clientX - dragOffset.current.x, window.innerWidth - pillW));
    const newPageY = Math.max(HEADER_HEIGHT, Math.min(pageY - dragOffset.current.y, HERO_SECTION_HEIGHT - pH - 10));
    setDragPos({ x: newX, pageY: newPageY });
  };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    isDragging.current = true;
    hasDragged.current = false;
    const rect = pillRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const onMove = (e) => { if (!isDragging.current) return; hasDragged.current = true; clampAndSet(e.clientX, e.clientY); };
    const onUp   = () => { isDragging.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    isDragging.current = true;
    hasDragged.current = false;
    const rect = pillRef.current.getBoundingClientRect();
    dragOffset.current = { x: t.clientX - rect.left, y: t.clientY - rect.top };
    const onMove = (e) => { e.preventDefault(); if (!isDragging.current) return; hasDragged.current = true; const t = e.touches[0]; clampAndSet(t.clientX, t.clientY); };
    const onEnd  = () => { isDragging.current = false; window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
  };

  // ── Story click ─────────────────────────────────────────────────────────────
  const handleStoryClick = (story) => {
    if (hasDragged.current) return;
    markViewed(story);
    setSelectedStory(story);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStory(null);
    fetchStatuses(); // refresh so view counts / rings update
  };

  const handlePrevStory = () => {
    const i = stories.findIndex(s => s.id === selectedStory.id);
    if (i > 0) { markViewed(stories[i - 1]); setSelectedStory(stories[i - 1]); }
  };

  const handleNextStory = () => {
    const i = stories.findIndex(s => s.id === selectedStory.id);
    if (i < stories.length - 1) { markViewed(stories[i + 1]); setSelectedStory(stories[i + 1]); }
    else handleCloseModal();
  };

  // ── Inline style for draggable pill ────────────────────────────────────────
  const getInlineStyle = () => {
    if (!dragPos) {
      const clampedTop = Math.min(420, heroBottomInViewport - pillH - 10);
      return clampedTop < HEADER_HEIGHT ? { display: 'none' } : {};
    }
    const viewportTop = dragPos.pageY - scrollY;
    if (viewportTop + pillH > heroBottomInViewport || heroBottomInViewport < HEADER_HEIGHT) return { display: 'none' };
    return {
      left: dragPos.x,
      top: Math.max(HEADER_HEIGHT, Math.min(viewportTop, heroBottomInViewport - pillH - 10)),
      transform: 'none',
    };
  };

  // ── Don't render at all if no stories (keeps hero clean) ───────────────────
  if (!loading && stories.length === 0) return null;

  return (
    <>
      {isHeroVisible && (
        <div
          ref={pillRef}
          className={styles.pill}
          style={getInlineStyle()}
          onMouseDown={onMouseDown}
          onTouchStart={onTouchStart}
        >
          <div className={styles.dragHandle}>⠿</div>
          <div className={styles.storiesRow}>

            {loading
              ? /* Skeleton placeholders */
                [1,2,3,4].map(i => (
                  <div key={i} className={styles.storyItem}>
                    <div className={styles.avatarRing} style={{ background: 'rgba(255,255,255,0.08)' }}>
                      <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: 'rgba(255,255,255,0.12)' }} />
                    </div>
                    <span className={styles.username} style={{ background: 'rgba(255,255,255,0.1)', color: 'transparent', borderRadius: 4 }}>••••</span>
                  </div>
                ))
              : stories.map(story => (
                  <div key={story.id} className={styles.storyItem} onClick={() => handleStoryClick(story)}>
                    <div className={`${styles.avatarRing} ${story.isNew ? styles.newRing : styles.seenRing}`}>
                      {/* ✅ FIXED: shows actual content thumbnail / video frame */}
                      <StoryAvatar story={story} className={styles.avatar} />
                      {story.isNew && <div className={styles.greenDot} />}
                    </div>
                    <span className={styles.username}>
                      {story.username.length > 10 ? story.username.slice(0, 9) + '…' : story.username}
                    </span>
                  </div>
                ))
            }

          </div>
        </div>
      )}

      {isModalOpen && selectedStory && (
        <StoryModal
          story={selectedStory}
          onClose={handleCloseModal}
          onPrev={handlePrevStory}
          onNext={handleNextStory}
          hasNext={stories.findIndex(s => s.id === selectedStory.id) < stories.length - 1}
          hasPrev={stories.findIndex(s => s.id === selectedStory.id) > 0}
        />
      )}
    </>
  );
};

export default StoriesSection;

















// import React, { useState, useRef, useEffect } from 'react';
// import StoryModal from './StoryModal';
// import styles from './StoriesSection.module.css';

// const MOCK_STORIES = [
//   { id: 1, username: 'imselling_off...', avatar: 'https://via.placeholder.com/80?text=IM', isNew: true,
//     stories: [{ id: 101, type: 'image', url: 'https://via.placeholder.com/600x1000/667eea/ffffff?text=Story+1', duration: 5000 }] },
//   { id: 2, username: 'electronics_ke', avatar: 'https://via.placeholder.com/80?text=EL', isNew: true,
//     stories: [{ id: 201, type: 'image', url: 'https://via.placeholder.com/600x1000/f093fb/ffffff?text=Electronics', duration: 5000 }] },
//   { id: 3, username: 'fashion_deals', avatar: 'https://via.placeholder.com/80?text=FD', isNew: true,
//     stories: [{ id: 301, type: 'image', url: 'https://via.placeholder.com/600x1000/4facfe/ffffff?text=Fashion', duration: 5000 }] },
//   { id: 4, username: 'vehicles_ke', avatar: 'https://via.placeholder.com/80?text=VE', isNew: false,
//     stories: [{ id: 401, type: 'image', url: 'https://via.placeholder.com/600x1000/fa709a/ffffff?text=Vehicles', duration: 5000 }] },
//   { id: 5, username: 'property_finds', avatar: 'https://via.placeholder.com/80?text=PF', isNew: true,
//     stories: [{ id: 501, type: 'image', url: 'https://via.placeholder.com/600x1000/30cfd0/ffffff?text=Property', duration: 5000 }] },
//   { id: 6, username: 'furniture_hub', avatar: 'https://via.placeholder.com/80?text=FH', isNew: true,
//     stories: [{ id: 601, type: 'image', url: 'https://via.placeholder.com/600x1000/a8edea/ffffff?text=Furniture', duration: 5000 }] },
//   { id: 7, username: 'farm_produce', avatar: 'https://via.placeholder.com/80?text=FP', isNew: false,
//     stories: [{ id: 701, type: 'image', url: 'https://via.placeholder.com/600x1000/fed6e3/ffffff?text=Farm', duration: 5000 }] },
//   { id: 8, username: 'tech_gadgets', avatar: 'https://via.placeholder.com/80?text=TG', isNew: true,
//     stories: [{ id: 801, type: 'image', url: 'https://via.placeholder.com/600x1000/c471f5/ffffff?text=Tech', duration: 5000 }] },
// ];

// const MAX_STORIES = 10;

// // ── Hero section height in px (distance from top of page to Featured Categories)
// // Adjust this to match exactly where your hero section ends
// const HERO_SECTION_HEIGHT = 520;
// const HEADER_HEIGHT = 70;

// const StoriesSection = () => {
//   const [stories] = useState(MOCK_STORIES.slice(0, MAX_STORIES));
//   const [selectedStory, setSelectedStory] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);

//   // dragPos stores absolute page position (relative to document top, not viewport)
//   const [dragPos, setDragPos] = useState(null); // null = use CSS default centering
//   const [scrollY, setScrollY] = useState(0);

//   const isDragging = useRef(false);
//   const dragOffset  = useRef({ x: 0, y: 0 });
//   const hasDragged  = useRef(false);
//   const pillRef     = useRef(null);

//   // ── Track scroll so we can hide pill when hero scrolls out of view ──────────
//   useEffect(() => {
//     const onScroll = () => setScrollY(window.scrollY);
//     window.addEventListener('scroll', onScroll, { passive: true });
//     return () => window.removeEventListener('scroll', onScroll);
//   }, []);

//   // ── Pill is only visible while hero section is still on screen ─────────────
//   // heroBottom (in viewport coords) = HERO_SECTION_HEIGHT - scrollY
//   const heroBottomInViewport = HERO_SECTION_HEIGHT - scrollY;
//   const pillH = pillRef.current?.offsetHeight || 100;
//   const isHeroVisible = heroBottomInViewport > HEADER_HEIGHT + pillH;

//   // ── Drag handlers ───────────────────────────────────────────────────────────
//   const onMouseDown = (e) => {
//     if (e.button !== 0) return;
//     e.preventDefault();
//     isDragging.current = true;
//     hasDragged.current = false;

//     const rect = pillRef.current.getBoundingClientRect();
//     dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };

//     const onMove = (e) => {
//       if (!isDragging.current) return;
//       hasDragged.current = true;
//       clampAndSet(e.clientX, e.clientY);
//     };
//     const onUp = () => {
//       isDragging.current = false;
//       window.removeEventListener('mousemove', onMove);
//       window.removeEventListener('mouseup', onUp);
//     };
//     window.addEventListener('mousemove', onMove);
//     window.addEventListener('mouseup', onUp);
//   };

//   const onTouchStart = (e) => {
//     const t = e.touches[0];
//     isDragging.current = true;
//     hasDragged.current = false;
//     const rect = pillRef.current.getBoundingClientRect();
//     dragOffset.current = { x: t.clientX - rect.left, y: t.clientY - rect.top };

//     const onMove = (e) => {
//       e.preventDefault();
//       if (!isDragging.current) return;
//       hasDragged.current = true;
//       const t = e.touches[0];
//       clampAndSet(t.clientX, t.clientY);
//     };
//     const onEnd = () => {
//       isDragging.current = false;
//       window.removeEventListener('touchmove', onMove);
//       window.removeEventListener('touchend', onEnd);
//     };
//     window.addEventListener('touchmove', onMove, { passive: false });
//     window.addEventListener('touchend', onEnd);
//   };

//   const clampAndSet = (clientX, clientY) => {
//     const pill = pillRef.current;
//     const pillW = pill?.offsetWidth || 400;
//     const pillH = pill?.offsetHeight || 100;

//     // clientY is viewport-relative; convert to page-absolute
//     const pageY = clientY + window.scrollY;

//     // Clamp X within viewport
//     const newX = Math.max(0, Math.min(
//       clientX - dragOffset.current.x,
//       window.innerWidth - pillW
//     ));

//     // ✅ Clamp Y: pill must stay within hero section (page-absolute coords)
//     // Min: just below header | Max: hero bottom minus pill height
//     const newPageY = Math.max(
//       HEADER_HEIGHT,
//       Math.min(pageY - dragOffset.current.y, HERO_SECTION_HEIGHT - pillH - 10)
//     );

//     setDragPos({ x: newX, pageY: newPageY });
//   };

//   const handleStoryClick = (story) => {
//     if (hasDragged.current) return;
//     setSelectedStory(story);
//     setIsModalOpen(true);
//   };

//   const handleCloseModal = () => { setIsModalOpen(false); setSelectedStory(null); };
//   const handlePrevStory  = () => { const i = stories.findIndex(s => s.id === selectedStory.id); if (i > 0) setSelectedStory(stories[i - 1]); };
//   const handleNextStory  = () => { const i = stories.findIndex(s => s.id === selectedStory.id); if (i < stories.length - 1) setSelectedStory(stories[i + 1]); else handleCloseModal(); };

//   // ✅ Convert page-absolute Y back to viewport Y for fixed positioning
//   // fixedTop = pageY - scrollY  → clamped so it never goes below hero in viewport
//   const getInlineStyle = () => {
//     if (!dragPos) {
//       // Default: CSS handles left:50% centering, top:420px
//       // But clamp top so it doesn't show below hero when scrolled
//       const clampedTop = Math.min(420, heroBottomInViewport - pillH - 10);
//       return clampedTop < HEADER_HEIGHT
//         ? { display: 'none' } // hero scrolled fully off — hide pill
//         : {}; // let CSS default handle it
//     }

//     // After drag: convert stored pageY to current viewport top
//     const viewportTop = dragPos.pageY - scrollY;

//     // If hero has scrolled above viewport, hide pill
//     if (viewportTop + pillH > heroBottomInViewport || heroBottomInViewport < HEADER_HEIGHT) {
//       return { display: 'none' };
//     }

//     return {
//       left: dragPos.x,
//       top: Math.max(HEADER_HEIGHT, Math.min(viewportTop, heroBottomInViewport - pillH - 10)),
//       transform: 'none',
//     };
//   };

//   return (
//     <>
//       {isHeroVisible && (
//         <div
//           ref={pillRef}
//           className={styles.pill}
//           style={getInlineStyle()}
//           onMouseDown={onMouseDown}
//           onTouchStart={onTouchStart}
//         >
//           <div className={styles.dragHandle}>⠿</div>
//           <div className={styles.storiesRow}>
//             {stories.map((story) => (
//               <div key={story.id} className={styles.storyItem} onClick={() => handleStoryClick(story)}>
//                 <div className={`${styles.avatarRing} ${story.isNew ? styles.newRing : styles.seenRing}`}>
//                   <img src={story.avatar} alt={story.username} className={styles.avatar} />
//                   {story.isNew && <div className={styles.greenDot} />}
//                 </div>
//                 <span className={styles.username}>{story.username}</span>
//               </div>
//             ))}
//           </div>
//         </div>
//       )}

//       {isModalOpen && selectedStory && (
//         <StoryModal
//           story={selectedStory}
//           onClose={handleCloseModal}
//           onPrev={handlePrevStory}
//           onNext={handleNextStory}
//           hasNext={stories.findIndex(s => s.id === selectedStory.id) < stories.length - 1}
//           hasPrev={stories.findIndex(s => s.id === selectedStory.id) > 0}
//         />
//       )}
//     </>
//   );
// };

// export default StoriesSection;




// force rebuild Tue Apr  7 05:57:23 EAST 2026
